import React from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import dayjs from 'dayjs';

import { recIdLabel, recIdLookupKind } from './recIdLabels';
import { splitRecIdList } from './reportPipeline';

const tx = (t, key, fallback) => {
    const value = t ? t(key) : null;
    return value && value !== key ? value : fallback;
};

/**
 * Multi-select for a report variable or document id: always the same
 * searchable list with checkboxes that stays open while picking, plus
 * "select all" / "clear". The ids still drive the query; the field and the
 * list show the readable value ({@code displayFn}), with the id muted beside
 * it in the list. {@code labelFn} is the search text (id and value).
 */
export const MultiSelectField = ({ label, options, value, onChange, labelFn, tagLabelFn, t }) => {
    const selected = Array.isArray(value) ? value : [];
    const displayFn = (option) => {
        const text = tagLabelFn ? tagLabelFn(option) : null;
        return text !== null && text !== undefined && String(text) !== '' ? String(text) : String(option.value);
    };
    const actions = (
        <Stack direction="row" spacing={0.5}>
            <Button size="small" sx={{ minWidth: 0, px: 1, textTransform: 'none' }}
                disabled={options.length === 0 || selected.length === options.length}
                onClick={() => onChange([...options])}>
                {tx(t, 'reports_select_all', 'Alles selecteren')}
            </Button>
            <Button size="small" sx={{ minWidth: 0, px: 1, textTransform: 'none' }}
                disabled={selected.length === 0}
                onClick={() => onChange([])}>
                {tx(t, 'reports_select_clear', 'Wissen')}
            </Button>
        </Stack>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                limitTags={4}
                options={options}
                value={selected}
                onChange={(_, newVal) => onChange(newVal)}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                getOptionLabel={labelFn}
                renderOption={(optionProps, option, { selected: isSelected }) => {
                    const text = displayFn(option);
                    const showId = text !== String(option.value);
                    return (
                        <li {...optionProps} key={option.value}>
                            <Checkbox
                                size="small"
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                sx={{ mr: 1, p: 0.25 }}
                                checked={isSelected}
                            />
                            <Box component="span" sx={{ flex: 1 }}>{text}</Box>
                            {showId && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {option.value}
                                </Typography>
                            )}
                        </li>
                    );
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Chip size="small" label={displayFn(option)} title={String(option.value)}
                            {...getTagProps({ index })} key={option.value} />
                    ))
                }
                renderInput={(params) => (
                    <TextField {...params} label={label}
                        placeholder={selected.length === 0 ? tx(t, 'reports_select_type', 'Typ om te zoeken…') : ''} />
                )}
                sx={{ width: '100%' }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{actions}</Box>
        </Box>
    );
};

/**
 * One document-id input ({RecId;n} slot) of a document report: a pick list
 * with checkboxes when the slot's column has one (coid = firms, magazijn =
 * stores, kassa = registers; descriptions shown, codes muted), else a comma
 * list (MyFashionManager multi-select). The value is the comma list either
 * way. Shared by the run dialog and the document preview.
 */
export const RecIdInput = ({ report, slot, lang, lookups, value, onChange, label, required, helperText, t }) => {
    const text = (label || recIdLabel(report, slot, lang)) + (required ? ' *' : '');
    const kind = recIdLookupKind(report, slot);
    const options = kind && lookups && Array.isArray(lookups[kind]) ? lookups[kind] : [];
    if (options.length > 0) {
        const selected = splitRecIdList(value)
            .map(val => options.find(o => o.value === val) || { value: val, label: '' });
        return (
            <MultiSelectField
                label={text}
                options={options}
                value={selected}
                onChange={(newVal) => onChange(newVal.map(o => o.value).join(','))}
                labelFn={(opt) => (opt.label ? `${opt.value} — ${opt.label}` : String(opt.value))}
                tagLabelFn={(opt) => opt.label}
                t={t}
            />
        );
    }
    return (
        <TextField
            label={text}
            size="small"
            fullWidth
            value={value || ''}
            helperText={helperText}
            onChange={(e) => onChange(e.target.value)}
        />
    );
};

/** Quick date choices; returns [start, end] dayjs values. */
const presetRange = (key) => {
    const today = dayjs().startOf('day');
    switch (key) {
        case 'today': return [today, today];
        case 'yesterday': return [today.subtract(1, 'day'), today.subtract(1, 'day')];
        case 'thisWeek': {
            // Monday-based week.
            const offset = (today.day() + 6) % 7;
            const start = today.subtract(offset, 'day');
            return [start, start.add(6, 'day')];
        }
        case 'thisMonth': return [today.startOf('month'), today.endOf('month').startOf('day')];
        case 'lastMonth': {
            const prev = today.subtract(1, 'month');
            return [prev.startOf('month'), prev.endOf('month').startOf('day')];
        }
        case 'thisYear': return [today.startOf('year'), today.endOf('year').startOf('day')];
        default: return [today, today];
    }
};

const PRESETS = [
    ['today', 'reports_date_today', 'Vandaag'],
    ['yesterday', 'reports_date_yesterday', 'Gisteren'],
    ['thisWeek', 'reports_date_thisWeek', 'Deze week'],
    ['thisMonth', 'reports_date_thisMonth', 'Deze maand'],
    ['lastMonth', 'reports_date_lastMonth', 'Vorige maand'],
    ['thisYear', 'reports_date_thisYear', 'Dit jaar'],
];

/**
 * Quick date picks. With a from/to pair (`range`) every preset fills both
 * dates; with a single date only the day presets are offered.
 */
export const DateQuickPicks = ({ range, onPick, t }) => {
    const items = range ? PRESETS : PRESETS.slice(0, 2);
    return (
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                {tx(t, 'reports_date_quick', 'Snelkeuze')}:
            </Typography>
            {items.map(([key, tkey, fallback]) => (
                <Chip key={key} size="small" variant="outlined" label={tx(t, tkey, fallback)}
                    onClick={() => onPick(presetRange(key))} />
            ))}
        </Box>
    );
};
